import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3" data-testid="privacy-brand-link">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
              <img src="/logo.png" alt="DocMania logo" className="h-8 w-8 object-contain" />
            </div>
            <span className="text-xl font-extrabold tracking-tight text-slate-900">
              <span className="text-blue-600">Doc</span>
              <span className="text-orange-500">Mania</span>
            </span>
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>
        <h1 className="text-3xl font-bold text-foreground mb-2" data-testid="privacy-title">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>

        <div className="prose prose-sm text-foreground/90 space-y-6">
          <section>
            <h2 className="text-lg font-semibold">1. Information We Collect</h2>
            <p>We collect information you provide directly: your email address, country, and account credentials when you register. When you use our services, we process the files and content you submit (documents, images, and business information) solely to generate the requested output. We also collect usage data such as service history and credit transactions.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">2. How We Use Your Information</h2>
            <p>Your information is used to: provide and improve our document generation services, manage your account and credits, process payments, communicate service updates, and ensure platform security.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">3. File Processing &amp; Storage</h2>
            <p>Files and content you submit are processed on secure servers. Generated outputs are stored to allow download and are retained only as long as necessary to provide the service. We do not access, read, or use the content of your files or documents for any purpose other than providing the requested service.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">4. Third-Party Service Providers</h2>
            <p>To deliver our services, we work with trusted third-party providers, including: payment processors (to complete transactions), cloud hosting and storage providers (to host and deliver services), AI-powered content generation providers (to assist with generating document drafts and suggestions), and email delivery providers (to send account and service communications). These providers process data only as needed to perform their function and are not authorized to use your data for any other purpose.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">5. Data Sharing</h2>
            <p>We do not sell your personal information. We share data only with the third-party providers described above, and as required by law.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">6. Data Security</h2>
            <p>We implement industry-standard security measures including encrypted connections (HTTPS), secure authentication, and access controls. However, no method of transmission over the internet is 100% secure.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">7. Children&apos;s Privacy</h2>
            <p>DocMania is not directed at children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact us so we can remove it.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">8. Your Rights</h2>
            <p>You may request access to, correction of, or deletion of your personal data by contacting us. You can delete your account at any time, which will remove your personal information from our systems.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">9. Data Retention</h2>
            <p>We retain your account information for as long as your account is active. Generated documents and uploaded files are retained only as long as necessary to provide the service, as described in Section 3 above.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. Continued use of the service after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">11. Contact</h2>
            <p>For privacy-related questions, contact us at <strong>support@docmania.co</strong>.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
